import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { toast, Toaster } from "sonner";
import { BadgeCheck, ChartPie } from "lucide-react";
import PublicImageUploadInput from "../components/PublicImageUploadInput";

export default function DoctorEnroll() {
    const { registerDoctor } = useAuth();
    const nav = useNavigate();
    const [s, setS] = useState({ doctor_revenue_share: 0.7, consultation_fee_online: 500, consultation_fee_offline: 700 });
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({
        email: "", password: "", name: "", phone: "", qualification: "BHMS",
        specialization: "Classical Homeopathy", experience_years: 0, city: "", state: "",
        clinic_name: "", clinic_address: "", bio: "", photo_url: "",
        online_consult: true, offline_consult: false, license_number: "",
    });
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

    useEffect(() => { api.get("/settings/public").then(r => setS(r.data)).catch(() => {}); }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!f.email || !f.password || !f.name || !f.phone || !f.city) return toast.error("Please fill required fields");
        setBusy(true);
        try {
            await registerDoctor({ ...f, experience_years: parseInt(f.experience_years || 0) });
            toast.success("Registered! Awaiting admin approval.");
            nav("/doctor");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not register");
        } finally { setBusy(false); }
    };

    const sharePct = Math.round((s.doctor_revenue_share || 0.7) * 100);
    const platformPct = 100 - sharePct;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-12 gap-12" data-testid="enroll-doctor-page">
            <Toaster position="top-center" />
            <div className="lg:col-span-5 space-y-6">
                <div className="label-eyebrow text-sky-700">For homeopaths</div>
                <h1 className="font-serif text-5xl text-stone-900 leading-tight">Build your practice with Hahnemann.</h1>
                <p className="text-sm text-stone-600 leading-relaxed">Reach patients across the country online — or near your clinic. Set your availability, manage appointments and grow your reputation.</p>

                <div className="border border-sky-700 bg-sky-50 p-6" data-testid="fee-split-card">
                    <div className="flex items-center gap-2 mb-3"><ChartPie className="w-4 h-4 text-sky-700" /><div className="label-eyebrow text-sky-700">Transparent fee split</div></div>
                    <div className="font-serif text-3xl text-stone-900">You keep <span className="text-sky-700">{sharePct}%</span></div>
                    <p className="text-sm text-stone-700 mt-2">Hahnemann sets the consultation fee. Doctors receive <span className="font-medium">{sharePct}%</span> of every paid consultation. Hahnemann retains {platformPct}% to operate the platform.</p>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="bg-white border border-sky-700 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Online</div>
                            <div className="font-serif text-xl text-stone-900">{formatINR(s.consultation_fee_online)} <span className="text-xs text-stone-500">total</span></div>
                            <div className="text-xs text-sky-700 mt-1">Your share: {formatINR(s.consultation_fee_online * s.doctor_revenue_share)}</div>
                        </div>
                        <div className="bg-white border border-sky-700 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Offline</div>
                            <div className="font-serif text-xl text-stone-900">{formatINR(s.consultation_fee_offline)} <span className="text-xs text-stone-500">total</span></div>
                            <div className="text-xs text-sky-700 mt-1">Your share: {formatINR(s.consultation_fee_offline * s.doctor_revenue_share)}</div>
                        </div>
                    </div>
                </div>

                <ul className="space-y-2 text-sm text-stone-700">
                    <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 mt-0.5 text-sky-700" />Verified profile after admin approval</li>
                    <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 mt-0.5 text-sky-700" />Manage online + offline appointments</li>
                    <li className="flex items-start gap-2"><BadgeCheck className="w-4 h-4 mt-0.5 text-sky-700" />Receive direct AI-Doctor referrals</li>
                </ul>
            </div>

            <form onSubmit={submit} className="lg:col-span-7 bg-white border border-stone-200 p-8 space-y-5">
                <h2 className="font-serif text-3xl text-stone-900">Doctor enrollment</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full name *" v={f.name} on={set("name")} t="full-name" />
                    <Field label="Email *" v={f.email} on={set("email")} type="email" t="email" />
                    <Field label="Phone *" v={f.phone} on={set("phone")} t="phone" />
                    <Field label="Password *" v={f.password} on={set("password")} type="password" t="password" />
                    <Field label="Qualification" v={f.qualification} on={set("qualification")} t="qualification" />
                    <Field label="Specialization" v={f.specialization} on={set("specialization")} t="specialization" />
                    <Field label="Experience (yrs)" v={f.experience_years} on={set("experience_years")} type="number" t="experience" />
                    <Field label="License number" v={f.license_number} on={set("license_number")} t="license" />
                    <Field label="City *" v={f.city} on={set("city")} t="city" />
                    <Field label="State" v={f.state} on={set("state")} t="state" />
                    <Field label="Clinic name" v={f.clinic_name} on={set("clinic_name")} t="clinic" />
                </div>
                <div className="border border-stone-200 p-4 bg-stone-50/50">
                    <PublicImageUploadInput
                        label="Profile photo"
                        value={f.photo_url}
                        onChange={(v) => setF((p) => ({ ...p, photo_url: v }))}
                        testId="enroll-photo"
                        aspect="1/1"
                        fallbackText="Photo"
                        hint="Upload a clear face photo OR paste a link to one (LinkedIn / clinic website). Helps patients recognise you."
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Clinic address</Label>
                    <Input value={f.clinic_address} onChange={set("clinic_address")} className="rounded-none border-stone-300 mt-1" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-stone-500">Bio</Label>
                    <Textarea value={f.bio} onChange={set("bio")} className="rounded-none border-stone-300 mt-1" rows={3} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                    <label className="flex items-center justify-between border border-stone-300 p-3">
                        <span className="text-sm">Offer online consults</span>
                        <Switch checked={f.online_consult} onCheckedChange={(v) => setF((p) => ({ ...p, online_consult: v }))} data-testid="online-toggle" />
                    </label>
                    <label className="flex items-center justify-between border border-stone-300 p-3">
                        <span className="text-sm">Offer offline (clinic) visits</span>
                        <Switch checked={f.offline_consult} onCheckedChange={(v) => setF((p) => ({ ...p, offline_consult: v }))} data-testid="offline-toggle" />
                    </label>
                </div>
                <p className="text-xs text-stone-500">By enrolling you accept that Hahnemann retains <strong>{platformPct}%</strong> of every consultation fee and you receive <strong>{sharePct}%</strong>.</p>
                <Button type="submit" disabled={busy} className="rounded-none w-full py-6 bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="enroll-submit">
                    {busy ? "Submitting…" : "Submit application"}
                </Button>
            </form>
        </div>
    );
}

const Field = ({ label, v, on, type = "text", t }) => (
    <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
        <Input type={type} value={v} onChange={on} className="rounded-none border-stone-300 mt-1" data-testid={`enroll-${t}`} />
    </div>
);
